import React, { cloneElement, useEffect, useMemo, useRef } from "react"
import PropTypes from "prop-types"
import { Popover } from "antd"
import { buildBookingPopoverModel } from "pagesV2/pro/Calendar/shared/bookingTileUtils"
import { useHoverCapable } from "pagesV2/pro/Calendar/shared/hooks/useHoverCapable"
import { useBookingPopover } from "./context"
import PopoverBody from "./PopoverBody"
import styles from "./index.module.css"

const HOVER_OPEN_DELAY_MS = 300

let popoverIdCounter = 0

const useStablePopoverId = () => {
  const idRef = useRef(null)
  if (!idRef.current) {
    popoverIdCounter += 1
    idRef.current = `booking-popover-${popoverIdCounter}`
  }
  return idRef.current
}

/**
 * Desktop-only hover/focus popover wrapping a single booking tile. Shared by
 * every calendar view (Day, Week, Month) so there is one popover implementation.
 * On touch devices it renders the tile untouched, so a tap goes straight to the
 * booking detail view.
 */
const BookingPopover = ({ booking, colors, popoverId, children }) => {
  const hoverCapable = useHoverCapable()
  const generatedId = useStablePopoverId()
  const id = popoverId || generatedId
  const { isOpen, requestOpen, close } = useBookingPopover(id)
  const openTimerRef = useRef(null)

  const model = useMemo(() => buildBookingPopoverModel(booking), [booking])
  // Tint the popover surface with the same colors the tile uses so the two read
  // as the same booking. Colors are runtime-computed, hence inline styles.
  const overlayInnerStyle = colors ? { borderColor: colors.borderColor } : undefined
  useEffect(() => () => clearTimeout(openTimerRef.current), [])

  if (!hoverCapable) return children

  const cancelOpenTimer = () => clearTimeout(openTimerRef.current)

  const handleMouseEnter = (event) => {
    cancelOpenTimer()
    openTimerRef.current = setTimeout(requestOpen, HOVER_OPEN_DELAY_MS)
    children.props.onMouseEnter?.(event)
  }

  const handleMouseLeave = (event) => {
    cancelOpenTimer()
    close()
    children.props.onMouseLeave?.(event)
  }

  const handleFocus = (event) => {
    requestOpen()
    children.props.onFocus?.(event)
  }

  const handleBlur = (event) => {
    cancelOpenTimer()
    close()
    children.props.onBlur?.(event)
  }

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      cancelOpenTimer()
      close()
    }
    children.props.onKeyDown?.(event)
  }

  // Close before the tile's own onClick opens the detail view so the popover
  // never lingers behind it.
  const handleClick = (event) => {
    cancelOpenTimer()
    close()
    children.props.onClick?.(event)
  }

  const trigger = cloneElement(children, {
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onKeyDown: handleKeyDown,
    onClick: handleClick,
    "aria-describedby": isOpen ? id : undefined
  })

  return (
    <Popover
      open={isOpen}
      trigger={[]}
      placement="top"
      arrow={false}
      autoAdjustOverflow
      destroyTooltipOnHide
      overlayClassName={styles.overlay}
      overlayInnerStyle={overlayInnerStyle}
      content={<PopoverBody id={id} model={model} accentColor={colors?.textColor} />}
    >
      {trigger}
    </Popover>
  )
}

BookingPopover.propTypes = {
  booking: PropTypes.object,
  colors: PropTypes.shape({
    backgroundColor: PropTypes.string,
    borderColor: PropTypes.string,
    textColor: PropTypes.string
  }),
  popoverId: PropTypes.string,
  children: PropTypes.element.isRequired
}

export default BookingPopover
